import React, { useEffect, useState } from "react";

import DashboardLineChart from "../../CommonComponents/Dashboard/DashboardLineChart/DashboardLineChart";
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";

import dayjs from "dayjs";
import { formatDateArrayWithHour } from "../../../utils/DateFormatter";

const SPORTSBOOK_OPTIONS = {
  "SPORTSBOOK TURNOVER": "sportsbook_turnover",
  "SPORTSBOOK GGR": "sportsbook_ggr",
};

function SportsBook({
  isApiRunning,
  setIsApiRunning,
  turnOverData,
  setTurnOverData,
  ggrData,
  setGgrData,
  isSportsBookDataFirstRender,
  sportsBooksSectionDates,
  setSportsBooksSectionDates,
}) {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken,
    true
  );

  const queryString = `start_date=${sportsBooksSectionDates[0].format(
    "YYYY-MM-DD"
  )}&end_date=${sportsBooksSectionDates[1].format("YYYY-MM-DD")}`;

  const [selected, setSelected] = useState("SPORTSBOOK TURNOVER");

  const sportsBookData =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? turnOverData
      : ggrData;

  const labels =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? formatDateArrayWithHour(Object.keys(sportsBookData.total_turnover))
      : formatDateArrayWithHour(Object.keys(sportsBookData.total_ggr));

  const totalData =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? Object.values(sportsBookData.total_turnover)
      : Object.values(sportsBookData.total_ggr);

  const sumTotal =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? sportsBookData.sum_turnover
      : sportsBookData.sum_ggr;

  const dstData =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? Object.values(sportsBookData.dst_turnover)
      : Object.values(sportsBookData.dst_ggr);
  const sumDst =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? sportsBookData.sum_dst_turnover
      : sportsBookData.sum_dst_ggr;

  const sportradarData =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? Object.values(sportsBookData.sportradar_turnover)
      : Object.values(sportsBookData.sportradar_ggr);
  const sumSportradar =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? sportsBookData.sum_sportradar_turnover
      : sportsBookData.sum_sportradar_ggr;

  const oddinData =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? Object.values(sportsBookData.oddin_turnover)
      : Object.values(sportsBookData.oddin_ggr);

  const sumOddin =
    SPORTSBOOK_OPTIONS[selected] === "sportsbook_turnover"
      ? sportsBookData.sum_oddin_turnover
      : sportsBookData.sum_oddin_ggr;

  const data2 = {
    labels: labels,
    datasets: [
      {
        label: "Total",
        data: totalData,
        sum: sumTotal,
        borderColor: "#6177E8",
        backgroundColor: "#6177E8",
        borderRadius: 1,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        label: "DST",
        data: dstData,
        sum: sumDst,
        borderColor: "#71BF8A",
        backgroundColor: "#71BF8A",
        borderRadius: 1,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        label: "Sportradar",
        data: sportradarData,
        sum: sumSportradar,
        borderColor: "#FD6F27",
        backgroundColor: "#FD6F27",
        borderRadius: 1,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        label: "Oddin",
        data: oddinData,
        sum: sumOddin,
        borderColor: "#EE72BC",
        backgroundColor: "#EE72BC",
        borderRadius: 1,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
    headers: [
      {
        title: "SPORTSBOOK TURNOVER",
        amount: turnOverData?.sum_turnover,
        percentage: turnOverData?.change_percentage_turnover,
      },
      {
        title: "SPORTSBOOK GGR",
        amount: ggrData?.sum_ggr,
        percentage: ggrData?.change_percentage_ggr,
      },
    ],
  };

  const getSportsBookData = async (queryString) => {
    setIsApiRunning(true);

    const url = "dashboard/finance/sportsbook-v2?" + queryString;

    try {
      const response = await PrivateAdminPanelInstance?.get(url);
      const data = response?.data.data;
      
      if (data) {
        const {
          ts_agg_data: {
            total_turnover,
            sportradar_turnover,
            dst_turnover,
            oddin_turnover,
            sum_sportradar_turnover,
            sum_dst_turnover,
            sum_oddin_turnover,
            sum_turnover,
            previous_sum_turnover,
            change_percentage_turnover,
            total_ggr,
            sportradar_ggr,
            dst_ggr,
            oddin_ggr,
            sum_sportradar_ggr,
            sum_dst_ggr,
            sum_oddin_ggr,
            sum_ggr,
            previous_sum_ggr,
            change_percentage_ggr,
          },
        } = data;

        setTurnOverData({
          total_turnover,
          sportradar_turnover,
          dst_turnover,
          oddin_turnover,
          sum_sportradar_turnover,
          sum_dst_turnover,
          sum_oddin_turnover,
          sum_turnover,
          previous_sum_turnover,
          change_percentage_turnover,
        });

        setGgrData({
          total_ggr,
          sportradar_ggr,
          dst_ggr,
          oddin_ggr,
          sum_sportradar_ggr,
          sum_dst_ggr,
          sum_oddin_ggr,
          sum_ggr,
          previous_sum_ggr,
          change_percentage_ggr,
        });
      }
    } catch (error) {
      console.log("error getting overall data", error);
    } finally {
      setIsApiRunning(false);
    }
  };

  useEffect(() => {
    if (isSportsBookDataFirstRender.current) {
      getSportsBookData(queryString);
      isSportsBookDataFirstRender.current = false;
    }
  }, []);

  return (
    <DashboardLineChart
      title={false}
      headers={data2.headers}
      data={data2}
      setSelected={setSelected}
      rightInfo={true}
      configOptions={SPORTSBOOK_OPTIONS}
      isApiRunning={isApiRunning}
      getData={getSportsBookData}
      dates={sportsBooksSectionDates}
      setDates={setSportsBooksSectionDates}
    />
  );
}

export default SportsBook;
